/**
 * 标签定义管理页面
 * Tag Definition Management Page
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  message,
  Popconfirm,
  Tag,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import tagService, { getCategoryStats } from '../services/tagService';
import type { TagDefinition, TagDefinitionCreate, TagDefinitionUpdate } from '../types/tag';
import { TAG_CATEGORIES } from '../types/tag';

const { TextArea } = Input;

const TagDefinitionManagement: React.FC = () => {
  // ============================================================
  // 状态管理
  // ============================================================

  const [tags, setTags] = useState<TagDefinition[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>();
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>();
  const [searchKeyword, setSearchKeyword] = useState<string>('');

  // 模态框状态
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTag, setEditingTag] = useState<TagDefinition | null>(null);
  const [form] = Form.useForm();

  // 统计数据
  const [categoryStats, setCategoryStats] = useState<any[]>([]);

  // ============================================================
  // 数据加载
  // ============================================================

  const loadTags = useCallback(async () => {
    setLoading(true);
    try {
      const result = await tagService.getTagList({
        page,
        page_size: pageSize,
        category: categoryFilter,
        is_active: activeFilter,
        search: searchKeyword || undefined,
      });
      setTags(result.items);
      setTotal(result.total);
    } catch (error: any) {
      message.error(error.message || '加载标签列表失败');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, categoryFilter, activeFilter, searchKeyword]);

  const loadCategoryStats = useCallback(async () => {
    try {
      const stats = await getCategoryStats();
      console.log('Category stats loaded:', stats);
      // 确保返回的是数组
      if (Array.isArray(stats)) {
        setCategoryStats(stats);
      } else {
        console.error('Category stats is not an array:', stats);
        setCategoryStats([]);
      }
    } catch (error: any) {
      console.error('Failed to load category stats:', error);
      // 设置默认的空数组，防止 .map 错误
      setCategoryStats([]);
    }
  }, []);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  useEffect(() => {
    loadCategoryStats();
  }, [loadCategoryStats]);

  // ============================================================
  // 表格列定义
  // ============================================================

  const columns: ColumnsType<TagDefinition> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '标签名',
      dataIndex: 'tag_name',
      key: 'tag_name',
      width: 150,
      render: (text, record) => (
        <Tag color={record.color}>{text}</Tag>
      ),
    },
    {
      title: '显示名称',
      dataIndex: 'display_name',
      key: 'display_name',
      width: 150,
    },
    {
      title: '分类',
      dataIndex: 'tag_category',
      key: 'tag_category',
      width: 120,
      render: (category) => {
        const info = TAG_CATEGORIES[category];
        return (
          <Tag color={info?.color}>{info?.name || category}</Tag>
        );
      },
      filters: Object.entries(TAG_CATEGORIES).map(([key, value]) => ({
        text: value.name,
        value: key,
      })),
    },
    {
      title: '说明',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '默认评分',
      dataIndex: 'default_score',
      key: 'default_score',
      width: 100,
      render: (score) => `${score}分`,
    },
    {
      title: '权重',
      dataIndex: 'weight',
      key: 'weight',
      width: 80,
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (active) => (
        <Tag color={active ? 'success' : 'default'}>
          {active ? '启用' : '禁用'}
        </Tag>
      ),
      filters: [
        { text: '启用', value: true },
        { text: '禁用', value: false },
      ],
    },
    {
      title: '排序',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 80,
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确认删除此标签？"
            onConfirm={() => handleDelete(record.id)}
            okText="确认"
            cancelText="取消"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ============================================================
  // 事件处理
  // ============================================================

  const handleAdd = () => {
    setEditingTag(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (tag: TagDefinition) => {
    setEditingTag(tag);
    form.setFieldsValue(tag);
    setModalVisible(true);
  };

  const handleDelete = async (tagId: number) => {
    try {
      await tagService.deleteTagDefinition(tagId);
      message.success('删除成功');
      loadTags();
    } catch (error: any) {
      message.error(error.message || '删除失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (editingTag) {
        // 更新
        const updateData: TagDefinitionUpdate = {
          display_name: values.display_name,
          description: values.description,
          icon: values.icon,
          color: values.color || undefined,
          default_score: values.default_score,
          weight: values.weight,
          is_active: values.is_active,
          sort_order: values.sort_order,
        };
        await tagService.updateTagDefinition(editingTag.id, updateData);
        message.success('更新成功');
      } else {
        // 创建
        const createData: TagDefinitionCreate = {
          tag_name: values.tag_name,
          tag_category: values.tag_category,
          display_name: values.display_name,
          description: values.description,
          icon: values.icon,
          color: values.color || undefined,
          default_score: values.default_score,
          weight: values.weight,
          is_active: values.is_active,
          sort_order: values.sort_order,
        };
        await tagService.createTagDefinition(createData);
        message.success('创建成功');

        // 创建成功后清除筛选条件，确保新标签可见
        setCategoryFilter(undefined);
        setActiveFilter(undefined);
        setSearchKeyword('');
      }

      setModalVisible(false);
      loadTags();
      loadCategoryStats();
    } catch (error: any) {
      if (error.errorFields) {
        // 表单验证错误
        return;
      }
      message.error(error.message || '操作失败');
    }
  };

  // ============================================================
  // 渲染
  // ============================================================

  return (
    <div style={{ padding: 24 }}>
      {/* 页面标题和操作栏 */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ margin: 0 }}>标签定义管理</h2>
          <div style={{ marginTop: 8, color: '#666' }}>
            统一管理所有标签配置（性格、兴趣、行为、成就、历史）
          </div>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadTags}>
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            新增标签
          </Button>
        </Space>
      </div>

      {/* 分类统计 */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 12 }}>
        {Array.isArray(categoryStats) && categoryStats.map((stat) => (
          <Tag key={stat.code} color={stat.color} style={{ padding: '4px 12px', fontSize: 14 }}>
            {stat.name}: {stat.count}
          </Tag>
        ))}
      </div>

      {/* 标签列表表格 */}
      <Table
        columns={columns}
        dataSource={tags}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1200 }}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条`,
          onChange: (page, pageSize) => {
            setPage(page);
            setPageSize(pageSize || 20);
          },
        }}
        onChange={(pagination, filters) => {
          // 处理筛选
          const categoryFilterValue = filters.tag_category as any;
          setCategoryFilter(categoryFilterValue?.[0]);
          const activeFilterValue = filters.is_active as any;
          setActiveFilter(activeFilterValue?.[0]);
        }}
      />

      {/* 新增/编辑模态框 */}
      <Modal
        title={editingTag ? '编辑标签' : '新增标签'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={600}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            default_score: 50,
            weight: 1.0,
            is_active: true,
            sort_order: 0,
          }}
        >
          <Form.Item
            name="tag_category"
            label="分类"
            rules={[{ required: true, message: '请选择分类' }]}
          >
            <Select
              placeholder="请选择分类"
              disabled={!!editingTag}
              options={Object.entries(TAG_CATEGORIES).map(([key, value]) => ({
                label: value.name,
                value: key,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="tag_name"
            label="标签名"
            rules={[
              { required: true, message: '请输入标签名' },
              { pattern: /^[^\s]+$/, message: '标签名不能包含空格' },
            ]}
          >
            <Input placeholder="如: 油嘴滑舌" disabled={!!editingTag} />
          </Form.Item>

          <Form.Item
            name="display_name"
            label="显示名称"
            rules={[{ required: true, message: '请输入显示名称' }]}
          >
            <Input placeholder="如: 油嘴滑舌" />
          </Form.Item>

          <Form.Item name="description" label="说明">
            <TextArea rows={3} placeholder="标签说明" />
          </Form.Item>

          <Form.Item name="icon" label="图标URL">
            <Input placeholder="图标URL" />
          </Form.Item>

          <Form.Item name="color" label="颜色">
            <Input />
          </Form.Item>

          <Form.Item name="default_score" label="默认评分" rules={[{ required: true }]}>
            <InputNumber min={0} max={100} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="weight" label="权重" rules={[{ required: true }]}>
            <InputNumber min={0.1} max={2.0} step={0.1} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="sort_order" label="排序序号">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="is_active" label="状态" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TagDefinitionManagement;
